import Cookies from "js-cookie"
import {useEffect, useState, memo} from "react"
import {useStore} from "react-redux"
import {useShoppingBagGetCallbackObservable} from "@next/eventservice"
import {usePageloadLifetimeStore} from "../hooks/usePageloadLifetimeStore"
import {
    SubscribeToFavouritesGet,
    SubscribeToFavouritesAdd,
    SubscribeToFavouritesRemove,
    LoadFavouritesApiData,
    SubscribeToShoppingBagGet,
    LoadShoppingBagApiData,
    SubscribeToShoppingBagAdd,
    SubscribeToShoppingBagAddCist,
    SubscribeToShoppingBagAddEVoucher,
    SubscribeToShoppingBagAddLinkedItem,
    SubscribeToShoppingBagAddMultiple,
    SubscribeToShoppingBagAddWarranty,
    SubscribeToShoppingBagRemove,
    SubscribeToShoppingBagUpdateSize,
    SubscribeToShoppingBagUpdateQuantity,
    SubscribeToShoppingBagAccept,
    SubscribeToShoppingBagReject,
    SubscribeToShoppingBagAddSample,
    SubscribeToShoppingBagRemoveSample,
    SubscribeToShoppingBagAddGiftCard,
    SubscribeToMasIdUpdatedEvent,
    SubscribeToRequestMasId,
    SubscribeCookieConsentValueChanged,
    SubscribeToShoppingBagAddCustomItem,
    SubscribeToShoppingBagAddPersonalisedItem,
    SubscribeCookiePolicyClose,
} from "../events"
import {PerformCountryRedirectCheck} from "../utils/countryRedirect"
import {getRealTimePersonalisationData, isAnonymous} from "../utils/realTimePersonalisation"

interface EventComponentProps {
    enableFavourites: boolean
    siteUrl: string
    updatedmasid: boolean
    realm: string
    territory: string
    language: string
    geolocationUrl: string
    geolocationVersion: number
    firstName: string
    cleanSiteUrl: string
    personalisationDataRetrievalEnabled: boolean
    personalisationDataRetrievalAPIVersion: string
    doCountrySelectorRedirect: boolean
    countrySelectorEndpoint: string
    enableMinibagNotification: boolean
    enableCookieConsent: boolean
}

export const EventComponent = memo<EventComponentProps>(
    ({
        enableFavourites,
        siteUrl,
        updatedmasid,
        realm,
        territory,
        language,
        geolocationUrl,
        geolocationVersion,
        firstName,
        cleanSiteUrl,
        personalisationDataRetrievalEnabled,
        personalisationDataRetrievalAPIVersion,
        doCountrySelectorRedirect,
        countrySelectorEndpoint,
        enableMinibagNotification,
        enableCookieConsent,
    }) => {
        const store = useStore()

        const [masIdHash, setMasIdHash] = usePageloadLifetimeStore<string>()
        const [localFirstName, setLocalFirstName] = useState(firstName)

        useShoppingBagGetCallbackObservable(data => {
            const countryRedirectCheck = async () => {
                await PerformCountryRedirectCheck(
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                    data.data?.CountryRedirect,
                    territory,
                    geolocationVersion,
                    siteUrl,
                    geolocationUrl,
                    doCountrySelectorRedirect,
                    countrySelectorEndpoint,
                )
            }

            const UCMClosed = Cookies.get("OptanonAlertBoxClosed") !== undefined
            // eslint-disable-next-line dot-notation
            if (typeof window["OptanonWrapper"] !== "undefined" && !UCMClosed) {
                // UCM may be invoked, subscribe to close event
                SubscribeCookiePolicyClose(() => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    countryRedirectCheck()
                })
            } else {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                countryRedirectCheck()
            }
        })

        // Set up event subscriptions immediately
        useEffect(() => {
            SubscribeToFavouritesGet(siteUrl)
            SubscribeToFavouritesAdd(siteUrl)
            SubscribeToFavouritesRemove(siteUrl)
            SubscribeToShoppingBagGet(siteUrl, store, enableMinibagNotification)
            SubscribeToShoppingBagAdd(siteUrl)
            SubscribeToShoppingBagAddCist(siteUrl)
            SubscribeToShoppingBagAddCustomItem(siteUrl)
            SubscribeToShoppingBagAddPersonalisedItem(siteUrl)
            SubscribeToShoppingBagAddEVoucher(siteUrl)
            SubscribeToShoppingBagAddLinkedItem(siteUrl)
            SubscribeToShoppingBagAddMultiple(siteUrl)
            SubscribeToShoppingBagAddWarranty(siteUrl)
            SubscribeToShoppingBagRemove(siteUrl)
            SubscribeToShoppingBagUpdateSize(siteUrl)
            SubscribeToShoppingBagUpdateQuantity(siteUrl)
            SubscribeToShoppingBagAccept(siteUrl)
            SubscribeToShoppingBagReject(siteUrl)
            SubscribeToShoppingBagAddSample(siteUrl)
            SubscribeToShoppingBagRemoveSample(siteUrl)
            SubscribeToShoppingBagAddGiftCard(siteUrl)
            LoadShoppingBagApiData(siteUrl, false, enableMinibagNotification)
            SubscribeToMasIdUpdatedEvent(masIdHash, setMasIdHash)
            if (enableFavourites) LoadFavouritesApiData(siteUrl)
            SubscribeCookieConsentValueChanged(siteUrl)
        }, [enableFavourites, language, masIdHash, realm, setMasIdHash, siteUrl, store, territory, updatedmasid])

        // Real-time personalisation data retrieval
        useEffect(() => {
            if (localFirstName !== firstName) {
                SubscribeToRequestMasId(
                    updatedmasid,
                    realm,
                    territory,
                    language,
                    cleanSiteUrl,
                    firstName,
                    !enableCookieConsent,
                )
                getRealTimePersonalisationData(
                    personalisationDataRetrievalEnabled,
                    personalisationDataRetrievalAPIVersion,
                    realm,
                    territory,
                    language,
                    cleanSiteUrl,
                    isAnonymous(firstName),
                    !enableCookieConsent,
                )
            }
            setLocalFirstName(firstName)
        }, [firstName])

        return null
    },
)
EventComponent.displayName = "EventComponent"
